import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';

import { RUNS_DEFAULT_PAGE_SIZE } from 'constants/aiProcessRuns';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';

import { GroupByMode } from './AiActivity.constants';

// The loan route template is
// `…/management/:tab?/:subtab?/:entityId?/:section?/:subsection?`. Supplying an
// execution id as `:entityId` WITHOUT a `:subtab` makes generatePath drop the
// empty optional and emit `…/ai-activity/<execId>` — which re-matches with the
// id bound to `:subtab`, leaving `:entityId` undefined and the drawer closed
// (DEV-3029). A stable `:subtab` placeholder forces the id into the `:entityId`
// slot, mirroring the FinancialInfo convention (it always pairs subtab+entityId).
export const AI_ACTIVITY_RUN_SUBTAB = 'run';

export const buildAiActivityPath = ({ applicationId, executionId } = {}) =>
  LoansPaths.applicationManagement.generatePath({
    [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
    [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.AI_ACTIVITY,
    [LOANS_ROUTING_PARAMS.subtab]: executionId ? AI_ACTIVITY_RUN_SUBTAB : null,
    [LOANS_ROUTING_PARAMS.entityId]: executionId ?? null,
  });

const URL_KEYS = [
  'status',
  'processId',
  'startedAfter',
  'startedBefore',
  'skip',
  'take',
  'search',
  'groupBy',
];

const NUMERIC_KEYS = new Set(['skip', 'take']);

const toInteger = value => {
  const parsed = Number.parseInt(value, 10);

  return Number.isFinite(parsed) ? parsed : null;
};

export const parseFiltersFromSearch = searchString => {
  const params = new URLSearchParams(searchString ?? '');

  return URL_KEYS.reduce((acc, key) => {
    const raw = params.get(key);

    if (_.isNil(raw) || raw === '') {
      return acc;
    }

    if (NUMERIC_KEYS.has(key)) {
      const parsed = toInteger(raw);

      return _.isNil(parsed) ? acc : { ...acc, [key]: parsed };
    }

    return { ...acc, [key]: raw };
  }, {});
};

const isDefaultValueFor = (key, value) => {
  if (key === 'skip') {
    return value === 0;
  }

  if (key === 'take') {
    return value === RUNS_DEFAULT_PAGE_SIZE;
  }

  if (key === 'groupBy') {
    return value === GroupByMode.DATE;
  }

  return _.isNil(value) || value === '';
};

export const stringifyFiltersToSearch = filters => {
  if (_.isNil(filters)) {
    return '';
  }

  const entries = URL_KEYS.reduce((acc, key) => {
    const value = filters[key];

    // Drop nil/empty BEFORE the default-value check. The skip/take/groupBy
    // branches of isDefaultValueFor compare against concrete defaults (0 /
    // RUNS_DEFAULT_PAGE_SIZE / DATE), so an `undefined` value slips past them
    // and would otherwise serialize to the literal string "undefined"
    // (`?skip=undefined&take=undefined&groupBy=undefined`) — which blanked the
    // run list because `groupBy=undefined` matches neither DATE nor PROCESS (DEV-3029).
    if (_.isNil(value) || value === '') {
      return acc;
    }

    if (isDefaultValueFor(key, value)) {
      return acc;
    }

    return [...acc, [key, String(value)]];
  }, []);

  return new URLSearchParams(entries).toString();
};
